import { ReactNode, useState } from 'react'
import { CommandList, CommandItem } from 'cmdk'
import { IconCheck } from '@tabler/icons-react'
import List from 'rc-virtual-list'

import { cn } from 'utils/classMerge'
import Tooltip from 'components/tooltip/Tooltip'
import { LoadingSpinner } from 'components/ui/loading/spinner'

import Text from '../../text'
import { ComboOption } from '../index'

interface ComboBoxOptionsProps {
  filteredOptions: ComboOption[]
  selectedOption?: ComboOption | null
  optionsAreLoading: boolean
  useVirtualList: boolean
  onSelect: (option: ComboOption) => void
  position?: 'top' | 'bottom'
}

const ComboBoxOptions: React.FC<ComboBoxOptionsProps> = ({
  filteredOptions,
  selectedOption,
  optionsAreLoading,
  useVirtualList,
  onSelect,
  position,
}) => {
  const [hoveredOptionId, setHoveredOptionId] = useState<number | null>(null)
  const [tooltipTimeout, setTooltipTimeout] = useState<NodeJS.Timeout | null>(
    null,
  )
  const [focusedItem, setFocusedItem] = useState<number>()

  const handleMouseEnter = (optionId: number) => {
    const timeout = setTimeout(() => {
      setHoveredOptionId(optionId)
    }, 1000)
    setTooltipTimeout(timeout)
  }

  const handleMouseLeave = () => {
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout)
    }
    setHoveredOptionId(null)
  }

  const renderTooltip = (
    content: ReactNode,
    tooltipContent: string,
    id: number,
  ) => (
    <Tooltip
      id={id}
      open={hoveredOptionId === id}
      text={tooltipContent}
      disabled={!tooltipContent}
      arrow
      position="top"
      theme="dark"
      boundary="viewport"
      popperOptions={{
        modifiers: {
          preventOverflow: {
            enabled: false,
          },
          flip: { enabled: false },
          hide: {
            enabled: false,
          },
        },
      }}
      width={240}
      content={content}
      delay={1000}
      clear={undefined}
      textStyle={undefined}
      elementRef={undefined}
    />
  )

  const renderIcon = (icon: ReactNode, id: number, tooltipContent?: string) => {
    if (tooltipContent) {
      return renderTooltip(<span>{icon}</span>, tooltipContent, id)
    }
    return <span>{icon}</span>
  }

  const renderOption = (option: ComboOption) => (
    <CommandItem
      key={option.id}
      value={option.label}
      onSelect={() => {
        onSelect(option)
      }}
      className={cn('tw-max-h-96, tw-cursor-pointer tw-py-2 tw-pl-3 tw-pr-4', {
        'tw-bg-orange-50': option.id === focusedItem,
        'tw-bg-orange-500 tw-text-white': selectedOption?.id === option.id,
        'tw-text-zinc-950': selectedOption?.id !== option.id,
      })}
      disabled={option.state === 'disabled'}
      onMouseEnter={() => {
        setFocusedItem(option.id)
        handleMouseEnter(option.id)
      }}
      onMouseLeave={() => {
        handleMouseLeave()
        setFocusedItem(undefined)
      }}
    >
      <div
        className={cn(
          'tw-flex tw-max-h-14 tw-w-full tw-items-center',
          option.state === 'disabled' && 'tw-text-zinc-500',
        )}
      >
        {option.icon &&
          renderIcon(option.icon, option.id, option.tooltipContent)}
        <div className="tw-ml-3 tw-flex tw-flex-col tw-text-left">
          <Text className="tw-m-0 tw-text-sm">{option.label}</Text>
          {option.helper && (
            <Text
              className={cn({
                'tw-m-0 tw-text-xs': true,
                'tw-text-white': selectedOption?.id === option.id,
                'tw-text-zinc-400': selectedOption?.id !== option.id,
              })}
            >
              {option.helper}
            </Text>
          )}
        </div>
        {selectedOption?.id === option.id && (
          <IconCheck
            className="tw-ml-auto tw-size-5 tw-text-white"
            aria-hidden="true"
          />
        )}
      </div>
    </CommandItem>
  )

  const renderList = () => {
    if (optionsAreLoading) {
      return (
        <div className="tw-flex tw-h-14 tw-items-center tw-justify-center">
          <LoadingSpinner className="tw-stroke-orange" />
        </div>
      )
    }

    if (useVirtualList) {
      return (
        <List itemKey="id" itemHeight={55} height={200} data={filteredOptions}>
          {(option) => renderOption(option)}
        </List>
      )
    }

    return filteredOptions.map((option) => renderOption(option))
  }

  return (
    <div
      className={cn(
        'tw-absolute tw-z-10 tw-w-full tw-rounded-md tw-border tw-border-zinc-300 tw-bg-white tw-pr-1 tw-shadow-md',
        {
          'tw-bottom-[calc(100%+36px)]': position === 'top',
        },
      )}
    >
      <CommandList className="tw-custom-scrollbar tw-mt-1 tw-max-h-80 tw-overflow-y-auto">
        {renderList()}
      </CommandList>
    </div>
  )
}

export default ComboBoxOptions
